<template>
  <v-row justify="center">
    <v-dialog
      v-if="selectedDeliveryStaff && selectedShiftsDay"
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="800"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar class="indigo">
            <v-icon dark>
              {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Header.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Header.Title') }}
            </div>

            <div class="text-caption grey--text">
              {{ selectedDeliveryStaff.Firstname }} {{ selectedDeliveryStaff.Lastname }}:
              <span class="grey--text text--darken-1">
                {{ selectedShiftsDay.ShiftsDate }}
              </span>
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-tabs
          v-model="activeTab"
          :grow="selectedShiftsDay.Shifts.length < 7"
          background-color="indigo"
          dark
          show-arrows
          slider-color="light-green"
        >
          <v-tab
            v-for="(shift, key) in selectedShiftsDay.Shifts"
            :key="'tab' + key"
            :href="'#tab' + key"
            active-class="v-tabs__item--active indigo darken-2"
            ripple
            @click="shiftToggle(shift)"
          >
            <template v-if="!shift.TimeEndedFmt">
              {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Header.Shift') }}<br>{{ shift.ShowDates ? shift.DateStartedFmt + ' ' + shift.TimeStartedFmt : shift.TimeStartedFmt }} -
              {{ $t('Common.Date.Now') }}
            </template>

            <template v-else>
              {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Header.Shift') }}<br>{{ shift.ShowDates ? shift.DateStartedFmt + ' ' + shift.TimeStartedFmt : shift.TimeStartedFmt }} -
              {{ shift.ShowDates ? shift.DateEndedFmt + ' ' + shift.TimeEndedFmt : shift.TimeEndedFmt }}
            </template>
          </v-tab>
        </v-tabs>

        <v-container
          class="pa-4"
          fluid
        >
          <v-row>
            <v-col
              class="text-left"
              cols="6"
              sm="3"
            >
              <div
                v-if="selectedShift"
                class="text-center"
              >
                <div class="text-caption pl-1 pt-0 blue--text">
                  <strong>{{ $t('Delivery.Common.ShiftStart') }}</strong>
                </div>
                <div class="text-h6 grey--text text--darken-1">
                  {{ selectedShift.ShiftStartMoney | currency }}
                </div>
                <div
                  class="text-caption pl-1 pt-1 grey--text"
                  style="white-space: nowrap;"
                >
                  {{ selectedShiftsDay.ShiftsDate }}
                </div>
                <div class="text-caption">
                  {{ selectedShift.TimeStartedFmt }}
                </div>
              </div>
            </v-col>

            <v-col
              class="text-left"
              cols="6"
              sm="3"
            >
              <div
                v-if="selectedShift"
                class="text-center"
              >
                <div class="text-caption pl-1 pt-0 orange--text">
                  <strong>{{ $t('Delivery.Common.ShiftTotal') }}</strong>
                </div>

                <div class="text-h6 grey--text text--darken-1">
                  {{ (selectedShift.ShiftStartMoney + selectedShift.ShiftOrdersTotal) | currency }}
                </div>

                <div
                  class="text-caption pl-1 pt-1"
                  style="white-space: nowrap;"
                >
                  <span class="grey--text">
                    {{ $t('Delivery.Common.Orders') }}
                  </span>
                  <div>{{ selectedShift.ShiftOrdersTotal | currency }}</div>
                </div>
              </div>
            </v-col>

            <v-col
              class="text-right"
              cols="12"
              sm="6"
            >
              <barcode
                :style="$vuetify.breakpoint.xsOnly ? 'zoom: 0.85' : ''"
                :text="(String(selectedDeliveryStaff.Firstname + ' ' + selectedDeliveryStaff.Lastname).toLocaleUpperCase($i18n.locale))"
                :value="getBarcode(selectedDeliveryStaff).barcode"
                class="print-item"
                element-tag="svg"
                font-size="12"
                format="CODE128"
                height="35"
                text-margin="2"
                text-position="bottom"
                width="1"
              />

              <div style="display: none;">
                <div
                  class="barcode-generator-print-element"
                  style="text-align: center;"
                >
                  <barcode
                    ref="barcodeCanvas"
                    :margin="0"
                    :value="getBarcode(selectedDeliveryStaff).barcode"
                    class="print-item"
                    element-tag="canvas"
                    font-size="12"
                    format="CODE128"
                    height="40"
                    text=" "
                    text-margin="2"
                    text-position="bottom"
                    width="1"
                  />
                </div>
              </div>

              <v-menu
                v-if="appMachinePrintersActive.length > 1 && appHasPrinters && userCanManage"
                left
                offset-y
                open-on-hover
              >
                <template #activator="{on}">
                  <v-btn
                    absolute
                    class="grey"
                    dark
                    icon
                    right
                    style="margin-top: 220px; top: -14px;"
                    top
                    v-on="on"
                  >
                    <v-icon>print</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-for="(printer, index) in appMachinePrintersActive"
                    :key="index"
                    @click="onPrintDeliveryShift(printer)"
                  >
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ printer.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-btn
                v-if="(appMachinePrintersActive.length === 1 && appHasPrinters) && userCanManage && posUser?.Settings?.AllUsers?.Permissions?.CanPrintDeliveryShift"
                absolute
                class="grey"
                dark
                icon
                right
                style="margin-top: 220px; top: -14px;"
                top
                @click.native="onPrintDeliveryShift(appMachinePrintersActive[0])"
              >
                <v-icon>print</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>

        <v-divider />

        <v-subheader class="pl-0 pt-4 grey--text text--darken-2">
          <v-spacer />

          <template v-if="selectedShift">
            {{ selectedShift.Orders.length > 0 ? $t('Delivery.Common.ShiftsOrders') + selectedShift.Orders.length : '' }}
          </template>
        </v-subheader>

        <v-card-text class="px-2">
          <v-tabs-items
            v-if="selectedShift"
            v-model="activeTab"
          >
            <v-tab-item
              :key="activeTab"
              :value="activeTab"
            >
              <v-container
                class="pa-0"
                fluid
              >
                <v-row>
                  <v-col
                    v-if="selectedShift.Orders.length <= 0"
                    cols="12"
                  >
                    <div class="text-center grey--text pa-6">
                      <v-icon
                        :color="$t('Delivery.DeliveryStaffDayShiftsDialog.Result.NoOrders.Color')"
                        class="mb-4"
                        size="128"
                      >
                        {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Result.NoOrders.Icon') }}
                      </v-icon>

                      <div
                        class="text-h6 mb-2"
                        style="height: auto;"
                      >
                        {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Result.NoOrders.Title') }}
                      </div>

                      <div
                        class="text-subtitle-1"
                        style="height: auto;"
                      >
                        {{ $t('Delivery.DeliveryStaffDayShiftsDialog.Result.NoOrders.Subtitle') }}
                      </div>
                    </div>
                  </v-col>

                  <v-col
                    v-else
                    cols="12"
                  >
                    <orders-v-list
                      :orders="selectedShift.Orders"
                      @list-tile-click="getOrderDetails"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import AppData from '../../mixins/appdata'
import Auth from '../../mixins/auth'
import BarcodeGenerator from '../../lib/barcode/BarcodeGenerator'
import OrdersVList from '../orders/common/OrdersVList'
import BarcodePrefixEnum from '@/api/Enums/BarcodePrefixEnum'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import DeliveryStaffCommon from '@/mixins/delivery/deliveryStaffCommon.js'

export default {
  components: {
    OrdersVList,
    barcode: BarcodeGenerator
  },
  mixins: [AppData, Auth, DeliveryStaffCommon],
  props : {
    visible: {
      type   : Boolean,
      default: false
    },
    tmpOrder: {
      type   : Object,
      default: undefined
    },
    selectedDeliveryStaff: {
      type   : Object,
      default: undefined
    },
    selectedShiftsDay: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      activeTab    : 'tab0',
      selectedShift: null
    }
  },
  computed: {
    tempOrder: {
      get() {
        return this.tmpOrder
      },
      set(val) {
        this.$emit('update:tmpOrder', val)
      }
    },
    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        if (this.selectedShiftsDay.Shifts.length > 0) {
          this.activeTab = 'tab'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.shiftToggle(this.selectedShiftsDay.Shifts[0])
              this.activeTab = 'tab0'
            })
          })
        }
      }
    }
  },
  methods: {
    shiftToggle(shift) {
      this.selectedShift = shift
    },
    getOrderListItemColor(order) {
      let colorClass = ''
      colorClass = order.Payment === PaymentTypeEnum.CASH ? 'green lighten-5' : 'blue-grey lighten-5'
      colorClass = parseInt(order.StatusId) !== 19 ? colorClass : 'red lighten-5'
      return colorClass
    },
    getOrderDetails(orderItem) {
      this.tempOrder = orderItem
      window.callAS(window.SocketCommand.Order.GetDetails, {
        order_id : orderItem.Id,
        status_id: orderItem.StatusId
      })
    },
    getBarcode(staff) {
      const barcode = {
        barcode: BarcodePrefixEnum.DeliveryStaff + staff.Id,
        value  : staff.Id
      }

      return barcode
    },
    onCancelButtonClick() {
      this.selectedShift = null
      this.isVisible = false
    }
  }
}
</script>
